import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProgereBrand } from "@/components/ProgereBrand";
import { TwoFactorCard } from "@/components/TwoFactorCard";
import {
  Crown, LayoutGrid, Users, LifeBuoy, ShieldCheck, ExternalLink, UserPlus, RefreshCw,
} from "lucide-react";

const ROLES = [
  ["platform_super_admin", "Super administrateur"],
  ["platform_support_manager", "Gestionnaire de support"],
  ["platform_support_technician", "Technicien de support"],
  ["platform_developer", "Développeur"],
  ["platform_security_admin", "Administrateur sécurité"],
  ["platform_billing_admin", "Administrateur facturation"],
  ["platform_legal_admin", "Administrateur légal"],
  ["platform_read_only_auditor", "Auditeur (lecture seule)"],
];
const MODULES = ["core", "punch", "crm", "erp", "compta", "paie", "devis-ai", "takeoff", "support"];
const card = "rounded-xl border border-white/10 bg-[#111217] p-5";
const inputCls = "w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#EAB308] focus:outline-none";

const TABS = [
  ["overview", "Tableau de bord", LayoutGrid],
  ["masters", "MASTER Centers", Crown],
  ["employees", "Employés internes", Users],
  ["support", "Support", LifeBuoy],
  ["audit", "Sécurité & audit", ShieldCheck],
];

export default function CreatorCenterPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [masters, setMasters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [audit, setAudit] = useState([]);
  const [invite, setInvite] = useState({ first_name: "", last_name: "", email: "", position: "", platform_role: "platform_support_technician", internal_modules: [] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/platform/overview").then(({ data }) => setOverview(data)).catch(() => {});
    api.get("/platform/master-centers").then(({ data }) => setMasters(data)).catch(() => {});
    api.get("/platform/employees").then(({ data }) => setEmployees(data)).catch(() => {});
    api.get("/support/v2/tickets?scope=all").then(({ data }) => setTickets(data)).catch(() => {});
    api.get("/platform/audit").then(({ data }) => setAudit(data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  if (user && !user.is_platform_owner) return <Navigate to="/app" replace />;

  const openMaster = (code) => navigate(`/app/master/${code}`);

  const toggleModule = (m) => setInvite((s) => ({
    ...s, internal_modules: s.internal_modules.includes(m) ? s.internal_modules.filter((x) => x !== m) : [...s.internal_modules, m],
  }));

  const sendInvite = async () => {
    if (!invite.first_name || !invite.last_name || !invite.email) { toast.error("Nom et courriel requis"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/platform/employees/invite", { ...invite, origin_url: window.location.origin });
      toast.success(data.email_sent ? "Invitation envoyée par courriel" : "Invitation créée (lien copié)");
      if (!data.email_sent && data.invite_link) navigator.clipboard?.writeText(data.invite_link);
      setInvite({ first_name: "", last_name: "", email: "", position: "", platform_role: "platform_support_technician", internal_modules: [] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };

  const Stat = ({ label, value, accent }) => (
    <div className={card}>
      <div className="text-xs uppercase tracking-wider text-white/45">{label}</div>
      <div className={`mt-1 text-3xl font-black ${accent ? "text-[#EAB308]" : "text-white"}`}>{value ?? "—"}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="creator-center-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#EAB308]/40 bg-[#EAB308]/10">
          <Crown className="w-5 h-5 text-[#EAB308]" />
        </div>
        <div>
          <h1 className="text-2xl font-black"><ProgereBrand module="CENTRE CRÉATEUR" size="medium" /></h1>
          <p className="text-sm text-white/50">Accès réservé au CRÉATEUR-PROPRIÉTAIRE</p>
        </div>
        <button onClick={load} className="ml-auto flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm text-white/60 hover:text-white"><RefreshCw className="w-4 h-4" /> Actualiser</button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-2">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`creator-tab-${id}`}
            className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${tab === id ? "bg-[#EAB308] text-black" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Clients" value={overview.clients} accent />
          <Stat label="Utilisateurs" value={overview.users} />
          <Stat label="Employés internes" value={overview.internal_users} />
          <Stat label="Contrats" value={overview.contracts} />
          <Stat label="Billets ouverts" value={overview.open_tickets} accent />
          <Stat label="Billets urgents" value={overview.urgent_tickets} />
          <Stat label="Incidents" value={overview.incidents} />
        </div>
      )}

      {tab === "masters" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {masters.map((m) => (
            <div key={m.code} className={card} data-testid={`master-card-${m.code}`}>
              <div className="flex items-center justify-between">
                <div className="text-lg font-black text-white uppercase">{m.name.replace(/^PROGÈRE\s*/i, "")}</div>
                <span className="rounded px-2 py-0.5 text-[11px] font-mono text-[#EAB308] bg-[#EAB308]/10">v{m.version}</span>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-white/50">
                <span>Billets ouverts : <b className="text-white">{m.open_tickets}</b></span>
                <span className="flex items-center gap-1 text-emerald-400"><span className="h-2 w-2 rounded-full bg-emerald-400" /> {m.status}</span>
              </div>
              <button onClick={() => openMaster(m.code)} data-testid={`open-master-${m.code}`}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-[#EAB308]/40 bg-[#EAB308]/10 px-4 py-2 text-sm font-semibold text-[#EAB308] hover:bg-[#EAB308]/20">
                <ExternalLink className="w-4 h-4" /> Ouvrir le MASTER
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "employees" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className={card}>
            <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-white"><UserPlus className="w-5 h-5 text-[#EAB308]" /> Inviter un employé</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Prénom" value={invite.first_name} onChange={(e) => setInvite({ ...invite, first_name: e.target.value })} data-testid="invite-first" />
                <input className={inputCls} placeholder="Nom" value={invite.last_name} onChange={(e) => setInvite({ ...invite, last_name: e.target.value })} data-testid="invite-last" />
              </div>
              <input className={inputCls} placeholder="Courriel (ex: technicien@progere.ca)" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email" />
              <input className={inputCls} placeholder="Poste (optionnel)" value={invite.position} onChange={(e) => setInvite({ ...invite, position: e.target.value })} />
              <select className={inputCls} value={invite.platform_role} onChange={(e) => setInvite({ ...invite, platform_role: e.target.value })} data-testid="invite-role">
                {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <div>
                <div className="mb-1 text-xs text-white/50">Modules autorisés</div>
                <div className="flex flex-wrap gap-2">
                  {MODULES.map((m) => (
                    <button key={m} onClick={() => toggleModule(m)} className={`rounded-md px-2.5 py-1 text-xs font-semibold uppercase ${invite.internal_modules.includes(m) ? "bg-[#EAB308] text-black" : "border border-white/15 text-white/50"}`}>{m}</button>
                  ))}
                </div>
              </div>
              <button onClick={sendInvite} disabled={saving} data-testid="invite-submit" className="w-full rounded-lg bg-[#EAB308] px-6 py-2.5 text-sm font-bold text-black hover:bg-[#ffd45c] disabled:opacity-50">
                {saving ? "Envoi…" : "Envoyer l'invitation"}
              </button>
            </div>
          </div>
          <div className={card}>
            <h2 className="mb-4 text-lg font-bold text-white">Employés ({employees.length})</h2>
            <div className="space-y-2">
              {employees.map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-lg border border-white/8 bg-black/30 px-4 py-2.5" data-testid={`employee-${e.email}`}>
                  <div>
                    <div className="text-sm font-semibold text-white">{e.name}</div>
                    <div className="text-xs text-white/40">{e.email}</div>
                  </div>
                  <span className="rounded px-2 py-0.5 text-[11px] font-semibold text-[#EAB308] bg-[#EAB308]/10">{e.role_label}</span>
                </div>
              ))}
              {employees.length === 0 && <p className="text-sm text-white/40">Aucun employé interne.</p>}
            </div>
          </div>
        </div>
      )}

      {tab === "support" && (
        <div className={card}>
          <h2 className="mb-4 text-lg font-bold text-white">Tous les billets ({tickets.length})</h2>
          <div className="space-y-2">
            {tickets.map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-lg border border-white/8 bg-black/30 px-4 py-2.5" data-testid={`ticket-row-${t.ref}`}>
                <div>
                  <div className="text-sm font-semibold text-white">{t.subject}</div>
                  <div className="text-xs font-mono text-[#EAB308]">{t.ref} · {t.module_code} · {t.priority}</div>
                </div>
                <span className="rounded px-2 py-0.5 text-[11px] text-white/70 bg-white/8">{t.status}</span>
              </div>
            ))}
            {tickets.length === 0 && <p className="text-sm text-white/40">Aucun billet.</p>}
          </div>
        </div>
      )}

      {tab === "audit" && (
        <div className="space-y-6">
          <TwoFactorCard />
          <div className={card}>
          <h2 className="mb-4 text-lg font-bold text-white">Journal d'audit</h2>
          <div className="space-y-1.5 font-mono text-xs">
            {audit.map((a) => (
              <div key={a.id} className="flex flex-wrap gap-2 border-b border-white/5 py-1.5 text-white/60">
                <span className="text-white/40">{new Date(a.created_at).toLocaleString("fr-CA")}</span>
                <span className="text-[#EAB308]">{a.action}</span>
                <span className="text-white/80">{a.target}</span>
                <span className="ml-auto text-white/30">{a.platform_user_email}</span>
              </div>
            ))}
            {audit.length === 0 && <p className="text-sm text-white/40">Aucune entrée.</p>}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
